import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';

import Markdown from '../../../../../Markdown';

import { formatStringForHookValue } from '../../../../../../utils/automationHookUtils';
import { Box, Text, useMedia } from '../../../../../Boneyard';
import { getPrice } from '@g4/next-furniture/Price/Price';
import { useTranslations } from '../../../../../../graphql/components/TranslationsProvider';
import { withValue } from '@g4/automation-hooks';
import { ancillaryTypes, tripflexCode } from '../../../../../../pageComponents/confirmationPage/flightInformation/constants';

const propTypes = {
  automationHook: PropTypes.string,
  flight: PropTypes.object,
  items: PropTypes.array,
  type: PropTypes.string,
  areNonBundledItems: PropTypes.bool,
  petInCabin: PropTypes.object,
  leg: PropTypes.string,
  isTripSummary: PropTypes.bool,
  travelerNumber: PropTypes.number,
  returningFlightExists: PropTypes.bool,
};

const defaultProps = {
  departing: '',
  returning: '',
  items: [],
};

const Container = styled(Box)`
  padding-left: ${props =>
    props.isTripSummary && props.isTablet
      ? props.isMobile
        ? '10px'
        : '12px'
      : props.isTripSummary
        ? '50px'
        : '75px'};
  padding-right: ${props =>
    props.isTripSummary && !props.isMobile
      ? props.isTablet
        ? '0px'
        : props.isTripSummary
          ? '50px'
          : '75px'
      : '0px'};
  padding-bottom: ${props =>
    props.isTripSummary && props.isMobile
      ? props.type === 'bags' || props.type === 'extras'
        ? '0px'
        : '10px'
      : '0px'};
  min-width: ${props =>
    props.isTripSummary && !props.isMobile ? '50%' : '100%'};
`;

const Value = styled(Text)`
  color: ${props =>
    props.isTripSummary
      ? props.theme.colors.cart.text
      : props.theme.colors.cart.travelerItem};
  padding-left: ${props =>
    props.isTripFlex ? props.theme.spacing(0) : props.theme.spacing(2)};
`;

const CodeContainer = styled(Text)`
  vertical-align: baseline;
  margin-top: ${props =>
    props.isTripSummary
      ? (props.type === 'bags' || props.type === 'extras') && props.isMobile
        ? '0px'
        : '9px'
      : '0px'};
`;

const ItemContainer = styled(Box)`
  flex-grow: 1;
  ${props => props.theme.query.md} {
    margin-bottom: ${props => props.theme.spacing(1)};
  }
  & + & {
    border-top: 1px solid ${props => props.theme.colors.cart.travelerSeparator};
  }
`;

const SeatTypeText = styled(Text)`
  padding-left: 5px;
  font-weight: bold;
`;

const TravelerItems = ({
  automationHook,
  flight,
  items,
  type,
  areNonBundledItems,
  petInCabin,
  leg,
  isTripSummary,
  travelerNumber,
  returningFlightExists,
}) => {
  const mq = useMedia();
  const isMobile = mq.lt('md');
  const isTablet = mq.lt('lg');

  const translations = useTranslations();
  const {
    travelerIncludedLabel,
    travelerDestinationConjoinFlightCodes,
    cartCheckedBagLabel,
    cartCheckedBagsLabel,
    cartCarryOnBagLabel,
    cartTripFlexLabel,
    cartPriorityAccessLabel,
    travelersNoSeatSelectedItem,
    petInCabinCartItemLabel,
    cartOnboardEntertainmentLabel,
    cartEnhancedTripFlexLabelHTML,
  } = isTripSummary ? translations.paymentPage : translations.cart;

  const bagTypes = {
    CHECK_IN_BAG: cartCheckedBagLabel,
    CHECK_IN_BAG_PREDEPARTURE: cartCheckedBagLabel,
    CHECK_IN_BAG_AIRPORT: cartCheckedBagLabel,
    CARRY_ON_BAG: cartCarryOnBagLabel,
    CARRY_ON_BAG_PREDEPARTURE: cartCarryOnBagLabel,
    CARRY_ON_BAG_AIRPORT: cartCarryOnBagLabel,
  };

  const hasEnhancedTripflex = items.some(item => item.ancillaryType === ancillaryTypes.TRIP_FLEX && item.code === tripflexCode.ENHANCED_TRIP_FLEX);

  const extrasTypes = {
    PRIORITY_BOARDING: cartPriorityAccessLabel,
    TRIP_FLEX: hasEnhancedTripflex ? cartEnhancedTripFlexLabelHTML : cartTripFlexLabel,
    ONBOARD_ENTERTAINMENT: cartOnboardEntertainmentLabel,
  };

  const getBagLabel = item => {
    if (
      item.quantity > 1 &&
      bagTypes[item.ancillaryType] === cartCheckedBagLabel
    ) {
      return item.quantity + ' ' + cartCheckedBagsLabel;
    }
    return item.quantity + ' ' + bagTypes[item.ancillaryType];
  };

  const shouldDisplayIncludedLabel = item => {
    return (
      // seatSizeId 7 is used to identify seats included in the new fare class bundle, and this is unique indentifier for new fare.
      ((item?.isBundledAncillaryIncluded || Number(item?.seatSizeId) === 7) && item?.price?.amount === 0)
    );
  };

  const isTripFlex = !isEmpty(
    items.filter(item => item.ancillaryType === 'TRIP_FLEX')
  );

  const isExtraItems = item => {
    return (
      !isTripSummary &&
      item.ancillaryType !== ancillaryTypes.ONBOARD_ENTERTAINMENT
    );
  };

  return (
    <Box
      d="flex"
      direction={isTripSummary ? 'column' : 'row'}
      minWidth={
        isTripSummary ? (returningFlightExists ? '50%' : '55%') : 'auto'
      }
    >
      <Container
        d="flex"
        automationHook={withValue(automationHook, leg)}
        isTripSummary={isTripSummary}
        isMobile={isMobile}
        isTablet={isTablet}
        type={type}
        isTripFlex={isTripFlex}
        leg={leg}
      >
        {(
          <CodeContainer
            size={isTripSummary ? (isMobile ? 12 : 16) : 12}
            weight={500}
            automationHook={withValue(automationHook, 'flight-codes')}
            lh={isTripSummary ? (isMobile ? 1.42 : 1.06) : 1.4}
            isTripSummary={isTripSummary}
            isMobile={isMobile}
            type={type}
          >
            {flight.origin.code} {travelerDestinationConjoinFlightCodes}{' '}
            {flight.destination.code}
          </CodeContainer>
        )}

        {/* TODO: Create 3 distinct components instead of using the type prop */}
        {type === 'seats' && (
          <ItemContainer>
            <Box
              d="flex"
              justifyContent="space-between"
              mt={isTripSummary ? 2 : 0}
            >
              {!!items[0] && items[0].row && items[0].column ? (
                <Value
                  size={
                    isTripSummary ? (isMobile ? 12 : 16) : isMobile ? 12 : 14
                  }
                  weight={isTripSummary ? 700 : 600}
                  automationHook={withValue(
                    withValue(withValue(automationHook, 'seat'), leg),
                    'label'
                  )}
                  lh={
                    isTripSummary
                      ? isMobile
                        ? 1.42
                        : 1.06
                      : isMobile
                        ? 1.42
                        : 1.35
                  }
                  isTripSummary={isTripSummary}
                >
                  <Box d="flex">
                    {items[0].row}
                    {items[0].column}
                  </Box>
                  {items[0].exitRow && (
                    <Box d="flex">
                      {translations.seatsPage.seatMapLegendExitRowLabel}
                    </Box>
                  )}
                </Value>
              ) : (
                <Value
                  size={isMobile ? 12 : 14}
                  weight={600}
                  automationHook={withValue(
                    withValue(withValue(automationHook, 'seat'), leg),
                    'label'
                  )}
                  lh={isMobile ? 1.42 : 1.35}
                  isTripSummary={isTripSummary}
                >
                  {travelersNoSeatSelectedItem}
                </Value>
              )}

              <Box
                d="flex"
                ml={isTripSummary ? 2 : 0}
                alignItems={isTripSummary ? 'center' : 'flex-start'}
                mr={isTripSummary && leg & isTablet === 'returning' ? 9 : 0}
              >
                <Text
                  size={isTripSummary && !isTablet ? 16 : isMobile ? 12 : 14}
                  automationHook={withValue(
                    withValue(withValue(automationHook, 'seat'), leg),
                    'price'
                  )}
                  lh={isTripSummary ? 1 : isMobile ? 1.42 : 1.35}
                  weight={isTripSummary && !isTablet ? 500 : 400}
                >
                  {shouldDisplayIncludedLabel(items[0])
                    ? travelerIncludedLabel
                    : getPrice(
                      items[0]?.seatPrice?.subtotal || items[0]?.price.amount
                    ).price}
                </Text>
              </Box>
            </Box>
          </ItemContainer>
        )}

        {type === 'bags' && (
          <ItemContainer>
            {items.map((item, index) => (
              <Box
                d="flex"
                justifyContent="space-between"
                key={index}
                mt={isTripSummary ? (isMobile ? 0 : 1) : 0}
              >
                <Value
                  size={
                    isTripSummary ? (isMobile ? 12 : 16) : isMobile ? 12 : 14
                  }
                  weight={isTripSummary ? 700 : 600}
                  automationHook={withValue(
                    withValue(
                      automationHook,
                      formatStringForHookValue(bagTypes[item.ancillaryType])
                    ),
                    'label'
                  )}
                  lh={
                    isTripSummary
                      ? isMobile
                        ? 1.42
                        : 1.5
                      : isMobile
                        ? 1.42
                        : 1.35
                  }
                  isTripSummary={isTripSummary}
                >
                  {getBagLabel(item)}
                </Value>
                <Box mt={isTripSummary ? 1 : 0} ml={isTripSummary ? 1 : 0}>
                  <Text
                    size={isTripSummary && !isTablet ? 16 : isMobile ? 12 : 14}
                    automationHook={withValue(
                      withValue(
                        automationHook,
                        formatStringForHookValue(bagTypes[item.ancillaryType])
                      ),
                      'price'
                    )}
                    lh={isTripSummary ? 1 : isMobile ? 1.42 : 1.35}
                    weight={isTripSummary && !isTablet ? 500 : 400}
                  >
                    {shouldDisplayIncludedLabel(item)
                      ? travelerIncludedLabel
                      : getPrice(item?.price.amount).price}
                  </Text>
                </Box>
              </Box>
            ))}
          </ItemContainer>
        )}

        {type === 'extras' && (
          <ItemContainer>
            {items.map((item, index) =>
              isTripSummary &&
                item.ancillaryType !== 'ONBOARD_ENTERTAINMENT' ? (
                <Box
                  d="flex"
                  justifyContent="space-between"
                  key={index}
                  mt={isMobile ? 0 : 1}
                >
                  <Value
                    size={isMobile ? 12 : 16}
                    weight={700}
                    automationHook={withValue(
                      withValue(
                        automationHook,
                        formatStringForHookValue(
                          extrasTypes[item.ancillaryType]
                        )
                      ),
                      'label'
                    )}
                    lh={isMobile ? 1.42 : 1.5}
                    isTripSummary={isTripSummary}
                  >
                    {extrasTypes[item.ancillaryType]}
                  </Value>
                  <Box mt={1} ml={1}>
                    <Text
                      size={isTablet ? (isMobile ? 12 : 14) : 16}
                      automationHook={withValue(
                        withValue(
                          automationHook,
                          formatStringForHookValue(
                            extrasTypes[item.ancillaryType]
                          )
                        ),
                        'price'
                      )}
                      weight={isTablet ? 400 : 500}
                    >
                      {shouldDisplayIncludedLabel(item)
                        ? travelerIncludedLabel
                        : getPrice(item?.price.amount).price}
                    </Text>
                  </Box>
                </Box>
              ) : (
                isExtraItems(item) && (
                  <Box d="flex" justifyContent="space-between" key={index}>
                    <Value
                      size={isMobile ? 12 : 14}
                      weight={600}
                      automationHook={withValue(
                        withValue(
                          automationHook,
                          formatStringForHookValue(
                            extrasTypes[item.ancillaryType]
                          )
                        ),
                        'label'
                      )}
                      lh={isMobile ? 1.42 : 1.35}
                      isTripSummary={isTripSummary}
                    >
                      {extrasTypes[item.ancillaryType]}
                    </Value>
                    <Box>
                      <Text
                        size={isMobile ? 12 : 14}
                        automationHook={withValue(
                          withValue(
                            automationHook,
                            formatStringForHookValue(
                              extrasTypes[item.ancillaryType]
                            )
                          ),
                          'price'
                        )}
                        lh={isMobile ? 1.42 : 1.35}
                      >
                        {shouldDisplayIncludedLabel(item)
                          ? travelerIncludedLabel
                          : getPrice(item?.price.amount).price}
                      </Text>
                    </Box>
                  </Box>
                )
              )
            )}
            {petInCabin && (
              <Box
                d="flex"
                justifyContent="space-between"
                mt={isTripSummary ? (isMobile ? 0 : 1) : 0}
              >
                <Value
                  size={
                    isTripSummary ? (isMobile ? 12 : 16) : isMobile ? 12 : 14
                  }
                  weight={isTripSummary ? 700 : 600}
                  automationHook={withValue(automationHook, 'pet-in-cabin')}
                  lh={
                    isTripSummary
                      ? isMobile
                        ? 1.42
                        : 1.5
                      : isMobile
                        ? 1.42
                        : 1.35
                  }
                  isTripSummary={isTripSummary}
                >
                  {petInCabinCartItemLabel}
                </Value>
                <Box mt={isTripSummary ? 1 : 0} ml={isTripSummary ? 1 : 0}>
                  <Text
                    size={isTripSummary && !isTablet ? 16 : isMobile ? 12 : 14}
                    automationHook={withValue(
                      withValue(automationHook, 'pet-in-cabin-price')
                    )}
                    lh={isTripSummary ? 1 : isMobile ? 1.42 : 1.35}
                    weight={isTripSummary && !isTablet ? 500 : 400}
                  >
                    {getPrice(petInCabin?.price.amount).price}
                  </Text>
                </Box>
              </Box>
            )}
          </ItemContainer>
        )}
      </Container>
      {items.map(
        (item, index) =>
          isTripSummary &&
          (item.ancillaryType === ancillaryTypes.ONBOARD_ENTERTAINMENT) &&
          ((leg === 'departing' && !isTablet) ||
            (leg === 'returning' && isTablet)) && (
            <Container
              d="flex"
              justifyContent="space-between"
              key={index}
              isTripSummary={isTripSummary}
              isMobile={isMobile}
              isTablet={isTablet}
              type={type}
              isTripFlex={isTripFlex}
            >
              <Value
                size={isMobile ? 12 : 16}
                weight={400}
                automationHook={withValue(
                  withValue(
                    automationHook,
                    formatStringForHookValue(extrasTypes[item.ancillaryType])
                  ),
                  'label'
                )}
                lh={isMobile ? 1.42 : 1.5}
                isTripSummary={isTripSummary}
                isTripFlex={isTripFlex}
              >
                {isTripSummary
                  ? extrasTypes[item.ancillaryType].toUpperCase()
                  : extrasTypes[item.ancillaryType]}
              </Value>
              <Box mt={1} ml={1}>
                <Text
                  size={isTablet ? (isMobile ? 12 : 14) : 16}
                  automationHook={withValue(
                    withValue(
                      automationHook,
                      formatStringForHookValue(extrasTypes[item.ancillaryType])
                    ),
                    'price'
                  )}
                  weight={isTablet ? 400 : 500}
                >
                  {shouldDisplayIncludedLabel(item) || travelerNumber > 1
                    ? travelerIncludedLabel
                    : getPrice(item?.price.amount).price}
                </Text>
              </Box>
            </Container>
          )
      )}
    </Box>
  );
};

TravelerItems.propTypes = propTypes;
TravelerItems.defaultProps = defaultProps;

export default TravelerItems;
